import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Target, TrendingUp, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ResultsWidget = ({ compact = false }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();
  
  // Check if user has premium/subscriber access (admin email or active subscription)
  const hasSubscriberAccess = isAuthenticated && (
    user?.email === 'admin@primestatlab.com' || 
    user?.subscription_status === 'active'
  );

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await axios.get(`${API}/results/summary`);
        setSummary(response.data);
      } catch (error) {
        console.error('Failed to fetch results:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  if (loading) {
    if (compact) {
      return (
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-4 bg-surface-highlight rounded w-24"></div>
        </div>
      );
    }
    return (
      <div className="bg-card border border-border p-8" data-testid="results-widget-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-surface-highlight rounded w-3/4"></div>
          <div className="h-12 bg-surface-highlight rounded"></div>
          <div className="h-12 bg-surface-highlight rounded"></div>
        </div>
      </div>
    );
  }

  if (!summary || summary.total_picks === 0) {
    if (compact) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground text-sm" data-testid="results-widget-empty">
          <Trophy size={16} />
          <span>No picks tracked yet</span>
        </div>
      );
    }
    return (
      <div className="bg-card border border-border p-8" data-testid="results-widget-empty">
        <h3 className="font-heading text-2xl font-bold mb-4">TRACK RECORD</h3>
        <p className="text-muted-foreground">No picks tracked yet.</p>
      </div>
    );
  }

  // Compact version for header - hide for non-subscribers
  if (compact) {
    if (!hasSubscriberAccess) {
      return null; // Don't show compact widget to non-subscribers
    }
    return (
      <div className="flex items-center gap-4 bg-card border border-border px-4 py-2 rounded" data-testid="results-widget-compact">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-primary" />
          <span className="font-mono text-sm font-bold">RECORD:</span>
        </div>
        <div className="flex items-center gap-3 font-mono text-sm">
          <span className="text-accent font-bold">{summary.wins}W</span>
          <span className="text-loss font-bold">{summary.losses}L</span>
          <span className="text-push font-bold">{summary.pushes}P</span>
        </div>
        <div className="text-primary font-mono font-bold text-sm">
          {summary.win_percentage}%
        </div>
      </div>
    );
  }

  // Full version - show subscribe prompt for non-subscribers
  if (!hasSubscriberAccess) {
    return (
      <div className="bg-card border border-border p-8" data-testid="results-widget-locked">
        <div className="flex items-center gap-2 mb-6">
          <Lock className="text-muted-foreground" size={24} />
          <h3 className="font-heading text-2xl font-bold">TRACK RECORD</h3>
        </div>
        <p className="text-muted-foreground mb-4">Subscribe to view our complete win/loss record</p>
        <Link to="/subscribe">
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono uppercase tracking-wider text-sm">
            Subscribe to Unlock
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border p-8" data-testid="results-widget">
      <div className="flex items-center gap-2 mb-6">
        <Trophy className="text-primary" size={24} />
        <h3 className="font-heading text-2xl font-bold">TRACK RECORD</h3>
      </div>
      
      <div className="space-y-6">
        <div className="text-center">
          <div className="font-mono text-5xl font-bold text-primary mb-2" data-testid="win-percentage">
            {summary.win_percentage}%
          </div>
          <div className="text-sm text-muted-foreground uppercase tracking-widest">Win Rate</div>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
          <div className="text-center">
            <div className="font-mono text-2xl font-bold text-accent mb-1" data-testid="wins-count">{summary.wins}</div>
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground uppercase">
              <Target size={12} />
              Wins
            </div>
          </div>
          <div className="text-center">
            <div className="font-mono text-2xl font-bold text-loss mb-1" data-testid="losses-count">{summary.losses}</div>
            <div className="text-xs text-muted-foreground uppercase">Losses</div>
          </div>
          <div className="text-center">
            <div className="font-mono text-2xl font-bold text-push mb-1" data-testid="pushes-count">{summary.pushes}</div>
            <div className="text-xs text-muted-foreground uppercase">Pushes</div>
          </div>
        </div>

        <div className="pt-6 border-t border-border text-center">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <TrendingUp size={16} />
            <span className="font-mono text-sm">{summary.total_picks} Total Picks</span>
          </div>
        </div>
      </div>
    </div>
  );
};